<template>
  <Transition name="statusbar-slide">
    <div
      v-if="shouldShow && !disconnectOverlayActive"
      class="status-bar"
      :class="stateClass"
      role="status"
      aria-live="polite"
    >
      <!-- Click-away backdrop for the detail popover -->
      <div v-if="detailOpen" class="sb-backdrop" @click="closeDetail" />

      <!-- Detail popover (click on the status region) -->
      <Transition name="popover-fade">
        <div v-if="detailOpen" class="popover" role="dialog" aria-label="Connection detail">
          <div class="pop-row"><span class="k">Connection</span><span class="v" :class="{ ok: statusState === 'healthy' }">{{ connectionLabel }}</span></div>
          <div class="pop-row"><span class="k">Uptime</span><span class="v">{{ durationLabel }}</span></div>
          <div class="pop-row"><span class="k">Layout</span><span class="v">{{ layoutLabel }}</span></div>
          <div class="pop-row"><span class="k">Pending</span><span class="v">{{ pendingCount }}</span></div>
          <div class="pop-row"><span class="k">Device</span><span class="v">{{ deviceName }}</span></div>
        </div>
      </Transition>

      <!-- see docs/EXTRACTED_COMMENTS.md#c056 -->
      <button
        class="sb-left"
        :aria-expanded="detailOpen"
        aria-label="Show connection detail"
        @click.stop="toggleDetail"
      >
        <span class="sb-dot" :class="`dot-${statusState}`" />
        <span class="sb-state">{{ statusText }}</span>
        <span v-if="statusState === 'healthy'" class="sb-sub">· {{ durationLabel }}</span>
      </button>

      <!-- Right: device + quiet gear -->
      <div class="sb-right">
        <span class="sb-device">{{ deviceName }}</span>
        <button
          class="sb-gear"
          aria-label="Open settings"
          @click.stop="openSettings"
          @keydown.enter.stop="openSettings"
          @keydown.space.prevent.stop="openSettings"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount, onMounted } from "vue";
import useStore from "../store";
import { ConnectionState } from "../socket/ConnectionState";
import UpdateCoordinator from "../utils/UpdateCoordinator";
import {connectionSupervisor} from "../services/managers";
import {storeToRefs} from "pinia";

const STATUS_BAR_HEIGHT = 64; // px - exported for layout calculation
const REVEAL_ZONE_HEIGHT = 4; // see docs/EXTRACTED_COMMENTS.md#c053
const REVEAL_DWELL_MS = 400; // see docs/EXTRACTED_COMMENTS.md#c054

const store = useStore();
const { loaded } = storeToRefs(store);

type StatusState = "error" | "offline" | "processing" | "healthy";

const debounceTimer = ref<number | null>(null);
const pointerInStatusZone = ref(false);
const detailOpen = ref(false);

const hasLayoutData = computed(() => {
  if (!loaded.value) return false;
  const ld: any = store.layoutData as any;
  return ld != null && Object.keys(ld).length > 0;
});

// ---------- Computed: Status State ----------
const statusState = computed<StatusState>(() => {
  // see docs/EXTRACTED_COMMENTS.md#c055
  void store.currentDate;

  // Priority 1: Error
  if (store.error && store.error.length > 0) {
    return "error";
  }

  // Priority 2: Offline (disconnected or reconnecting)
  const state = store.connection.state;
  if (
    state === ConnectionState.Disconnected ||
    state === ConnectionState.Connecting ||
    state === ConnectionState.Authenticating
  ) {
    return "offline";
  }

  // Priority 3: Processing (pending updates or layout loading)
  const pending = store.socketManager.getQueueLength();
  if (!hasLayoutData.value) {
    return "processing";
  }


  if (pending > 0) {
    return "processing";
  }

  const pending_ui = UpdateCoordinator.pendingCount.value;
  if (pending_ui > 0) {
    return "processing";
  }

  // Priority 4: Healthy
  return "healthy";
});

const stateClass = computed(() => `state-${statusState.value}`);

// ---------- Computed: Status Text ----------
const statusText = computed(() => {
  // see docs/EXTRACTED_COMMENTS.md#c358
  void store.currentDate;
  switch (statusState.value) {
    case "error":
      if (store.error == "websocket error") {
        const retryIn = getRetryCountdown();
        return retryIn ? `Offline - Retry in ${retryIn}s` : "Reconnecting...";
      }
      return truncate(store.error || "Unknown error", 70);
    case "offline":
      const retryIn = getRetryCountdown();
      return retryIn ? `Offline - Retry in ${retryIn}s` : "Reconnecting...";
    case "processing":
      if (!hasLayoutData.value) return "Loading layout...";
      const pending = store.socketManager.getQueueLength();
      if (pending > 0) return `Processing ${pending} update${pending !== 1 ? 's' : ''}`;
      const pending_ui = UpdateCoordinator.pendingCount.value;
      if (pending_ui > 0) return `Building ${pending_ui} update${pending_ui !== 1 ? 's' : ''}`;

      return store.loaded ? "Loading..." : "Loading layout...";
    case "healthy":
      return "Connected";
  }
});

const truncate = (str: string, max: number): string => {
  return str.length > max ? str.slice(0, max - 1) + "..." : str;
};

const getRetryCountdown = (): number | null => {
  const nextRetryAt = connectionSupervisor.nextRetryAt.value;
  if (nextRetryAt == null) return null;
  const remaining = Math.ceil((nextRetryAt - store.currentDate) / 1000);
  return remaining > 0 ? remaining : null;
};

// ---------- Computed: Detail Values ----------
const connectionLabel = computed(() => {
  const state = store.connection.state;
  switch (state) {
    case ConnectionState.Ready:
    case ConnectionState.Syncing:
      return "Online";
    case ConnectionState.Connecting:
    case ConnectionState.ConnectedUnverified:
      return "Connecting";
    case ConnectionState.Authenticating:
      return "Authenticating";
    case ConnectionState.Degraded:
      return "Degraded";
    default:
      return "Offline";
  }
});

const pendingCount = computed(() => {
  // see docs/EXTRACTED_COMMENTS.md#c359
  void store.currentDate;
  const socketPending = store.socketManager.getQueueLength();
  const coordinatorPending = UpdateCoordinator.pendingCount.value;
  if (socketPending !== 0) return socketPending;
  return coordinatorPending;
});

const layoutLabel = computed(() => {
  if (store.layout == null) return "No layout";
  if (!hasLayoutData.value) return "Loading...";
  return store.loaded ? `#${store.layout}` : "Loading...";
});

const deviceName = computed(() => {
  const deviceId = store.socketManager.getDeviceId();
  if (!deviceId) return "Unknown";
  return deviceId;
});


// ---------- Duration Timer ----------
const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return "0s";
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const durationLabel = computed(() => {
  if (store.connected && store.connection.connectedAt != null) {
    const duration = store.currentDate - store.connection.connectedAt;
    return formatDuration(duration);
  }
  if (!store.connected && store.connection.lastDisconnectedAt != null) {
    const duration = store.currentDate - store.connection.lastDisconnectedAt;
    return formatDuration(duration);
  }
  return "0s";
});

// ---------- Visibility Logic ----------
const disconnectOverlayActive = computed(() => store.disconnectPolicyDisplay.visible);

const shouldShow = computed(() => {
  // Always show if auto-shown due to error/offline
  if (store.ui.statusBarAutoShown) return true;
  // Otherwise respect user preference
  if (store.ui.statusBarVisible) return true;
  // Reveal while pointer is over the bottom status-bar region.
  return pointerInStatusZone.value;
});

// Auto-show on error or offline
watch(
  () => [statusState.value, store.disconnectPolicyDisplay.visible],
  ([state, overlayVisible]) => {
    // Don't auto-show if disconnect overlay is active
    if (overlayVisible) return;
      console.warn(state);

    store.ui.statusBarAutoShown = state === "error" || state === "offline";
  },
  { immediate: true }
);


// ---------- Interactions ----------
const openSettings = () => {
  if (store.ui.settingsOpen) return; // Already open
  store.ui.settingsOpen = true;
};

const toggleDetail = () => {
  detailOpen.value = !detailOpen.value;
};

const closeDetail = () => {
  detailOpen.value = false;
};

// Keyboard shortcut: Ctrl+Shift+S or F10
const handleKeydown = (event: KeyboardEvent) => {
  const isToggleShortcut =
    (event.ctrlKey && event.shiftKey && event.key === "S") ||
    event.key === "F10";

  if (!isToggleShortcut) return;

  event.preventDefault();

  // Can't hide if in error/offline state
  if ((statusState.value === "error" || statusState.value === "offline") && store.ui.statusBarVisible) {
    return;
  }

  store.ui.statusBarVisible = !store.ui.statusBarVisible;
  if (!store.ui.statusBarVisible) {
    store.ui.statusBarAutoShown = false;
  }
};

const updatePointerZone = (event: MouseEvent) => {
  if (pointerInStatusZone.value) {
    // see docs/EXTRACTED_COMMENTS.md#c360
    const hideZoneTop = window.innerHeight - STATUS_BAR_HEIGHT;
    if (event.clientY < hideZoneTop) clearPointerZone();
    return;
  }

  // see docs/EXTRACTED_COMMENTS.md#c361
  const armZoneTop = window.innerHeight - REVEAL_ZONE_HEIGHT;
  const inArmZone = event.clientY >= armZoneTop;

  if (inArmZone) {
    if (debounceTimer.value !== null) return; // dwell timer already running
    // see docs/EXTRACTED_COMMENTS.md#c362
    debounceTimer.value = window.setTimeout(() => {
      pointerInStatusZone.value = true;
      debounceTimer.value = null;
    }, REVEAL_DWELL_MS);
  } else {
    clearPointerZone();
  }
};

const clearPointerZone = () => {
  if (debounceTimer.value !== null) {
    clearTimeout(debounceTimer.value);
    debounceTimer.value = null;
  }
  pointerInStatusZone.value = false;
  detailOpen.value = false;
};

onMounted(() => {
  window.addEventListener("mousemove", updatePointerZone);
  window.addEventListener("mouseleave", clearPointerZone);
});


onBeforeUnmount(() => {
  window.removeEventListener("mousemove", updatePointerZone);
  window.removeEventListener("mouseleave", clearPointerZone);
  window.removeEventListener("keydown", handleKeydown);
  if (debounceTimer.value !== null) clearTimeout(debounceTimer.value);
});

// Export height for layout calculation
defineExpose({ STATUS_BAR_HEIGHT });
</script>

<style scoped>
.status-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  z-index: 9000;
  /* see docs/EXTRACTED_COMMENTS.md#c312 */
  background: var(--muted);
  border-top: 1px solid var(--border);
  box-shadow: 0 -8px 24px rgba(16, 24, 40, 0.10);
  font-family: var(--ui);
  font-size: 13.5px;
  color: var(--ink);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
}

/* Left: status (clickable) */
.sb-left {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  background: none;
  border: none;
  padding: 4px 6px;
  margin-left: -6px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font: inherit;
  color: var(--ink);
}

.sb-left:hover {
  background: rgba(0, 2, 37, 0.05);
}

.sb-left:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring);
}

.sb-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--ok);
}

.dot-healthy    { background: var(--ok); }
.dot-processing { background: var(--warn); }
.dot-error,
.dot-offline    { background: var(--err); }

.sb-state {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sb-sub {
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Right: device + gear */
.sb-right {
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--ink-3);
  font-size: 12.5px;
  min-width: 0;
}

.sb-device {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
  font-variant-numeric: tabular-nums;
}

.sb-gear {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  border-radius: var(--r-sm);
  color: var(--ink-3);
  cursor: pointer;
  flex-shrink: 0;
}

.sb-gear svg {
  width: 17px;
  height: 17px;
}

.sb-gear:hover {
  color: var(--ink);
  background: rgba(0, 2, 37, 0.05);
}

.sb-gear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring);
}

/* Detail popover */
.sb-backdrop {
  position: fixed;
  inset: 0;
  bottom: 40px;
  z-index: 1;
}

.popover {
  position: absolute;
  left: 12px;
  bottom: calc(100% + 8px);
  z-index: 2;
  border: 1px solid var(--border);
  background: var(--bg);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-lg);
  width: 270px;
  overflow: hidden;
}

.pop-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 15px;
  font-size: 13px;
  border-bottom: 1px solid var(--border);
}

.pop-row:last-child {
  border-bottom: 0;
}

.pop-row .k {
  color: var(--ink-3);
}

.pop-row .v {
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.pop-row .v.ok {
  color: var(--ok);
  font-weight: 600;
}

.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: opacity 150ms var(--ease), transform 150ms var(--ease);
}

.popover-fade-enter-from,
.popover-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

/* Slide transition */
.statusbar-slide-enter-active,
.statusbar-slide-leave-active {
  transition: transform 200ms var(--ease), opacity 200ms var(--ease);
}

.statusbar-slide-enter-from,
.statusbar-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .statusbar-slide-enter-active,
  .statusbar-slide-leave-active,
  .popover-fade-enter-active,
  .popover-fade-leave-active {
    transition: none !important;
  }
}
</style>
